import { useEffect, useMemo, useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Tabs, NumberInput, Tooltip,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff, IconPhoto,
  IconArrowUp, IconArrowDown, IconDeviceFloppy, IconShieldCheck, IconBuildingBank,
} from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete, confirmAction } from '@/lib/confirm'
import { useInsurancePartners, useInsuranceSteps } from '@/lib/swr'
import type { InsurancePartner, InsuranceStep, InsuranceType } from '@/types'

export default function InsurancePage() {
  return (
    <Stack>
      <Title order={2}>Даатгал</Title>
      <Card withBorder>
        <Tabs defaultValue="private">
          <Tabs.List>
            <Tabs.Tab value="private" leftSection={<IconShieldCheck size={14} />}>Хувийн ЭМД</Tabs.Tab>
            <Tabs.Tab value="public" leftSection={<IconBuildingBank size={14} />}>Улсын ЭМД</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="private" pt="md">
            <Stack gap="xl">
              <PartnersEditor type="private" sectionTitle="Даатгалын компаниуд" />
              <StepsEditor type="private" sectionTitle="Алхамууд (Хувийн ЭМД)" />
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="public" pt="md">
            <Stack gap="xl">
              <PartnersEditor type="public" sectionTitle="Үйлчилгээ" />
              <StepsEditor type="public" sectionTitle="Алхамууд (Улсын ЭМД)" />
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  )
}

function pickFile(onFile: (file: File) => void) {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = 'image/*'
  inp.onchange = () => inp.files?.[0] && onFile(inp.files[0])
  inp.click()
}

// ─── Partners Editor ────────────────────────────────────────────────────────

interface PartnerFormData { name: string; description: string; logo: string; order: number }
const partnerEmpty: PartnerFormData = { name: '', description: '', logo: '', order: 0 }

function PartnersEditor({ type, sectionTitle }: { type: InsuranceType; sectionTitle: string }) {
  const { data, isLoading, mutate } = useInsurancePartners()
  const all: InsurancePartner[] = useMemo(() => data?.data ?? [], [data])
  const [items, setItems] = useState<InsurancePartner[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    setItems([...all].filter((i) => i.type === type).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [all, type])

  const moveUp = (i: number) => {
    if (i === 0) return
    setItems((prev) => { const n = [...prev]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n })
    setOrderChanged(true)
  }
  const moveDown = (i: number) => {
    if (i === items.length - 1) return
    setItems((prev) => { const n = [...prev]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; return n })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу?', async () => {
    setReordering(true)
    try {
      await api.admin.reorderInsurancePartners(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<PartnerFormData>({ defaultValues: partnerEmpty })
  const watchLogo = watch('logo')

  const create = () => { setEditId(null); reset(partnerEmpty); setOpen(true) }
  const edit = (i: InsurancePartner) => {
    setEditId(i._id)
    reset({ name: i.name, description: i.description, logo: i.logo, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(partnerEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateInsurancePartner(editId, { ...form, type })
      else await api.admin.createInsurancePartner({ ...form, type })
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => {
    try { await api.admin.deleteInsurancePartner(id); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const toggle = async (i: InsurancePartner) => {
    try { await api.admin.updateInsurancePartnerStatus(i._id, !i.isActive); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('logo', r.url, { shouldDirty: true }) }
    catch { toast.error('Зураг алдаа') }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>{sectionTitle} ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Лого</Table.Th><Table.Th>Нэр</Table.Th><Table.Th>Тайлбар</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td>{i.logo ? <img src={i.logo} alt="" style={{ height: 32, objectFit: 'contain' }} /> : '—'}</Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.name}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{i.description}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>

      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="name" control={control} rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }} render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />} />
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={2} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <div>
              <Text size="sm" fw={500} mb={4}>Лого</Text>
              <Group>
                <Controller name="logo" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchLogo && <img src={watchLogo} alt="" style={{ marginTop: 8, maxHeight: 60 }} />}
            </div>
            <Group justify="flex-end">
              <Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Steps Editor ───────────────────────────────────────────────────────────

interface StepFormData { step: string; title: string; description: string; order: number }
const stepEmpty: StepFormData = { step: '', title: '', description: '', order: 0 }

function StepsEditor({ type, sectionTitle }: { type: InsuranceType; sectionTitle: string }) {
  const { data, isLoading, mutate } = useInsuranceSteps()
  const all: InsuranceStep[] = useMemo(() => data?.data ?? [], [data])
  const [items, setItems] = useState<InsuranceStep[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)
  useEffect(() => {
    setItems([...all].filter((i) => i.type === type).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [all, type])

  const moveUp = (i: number) => {
    if (i === 0) return
    setItems((prev) => { const n = [...prev]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n })
    setOrderChanged(true)
  }
  const moveDown = (i: number) => {
    if (i === items.length - 1) return
    setItems((prev) => { const n = [...prev]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; return n })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу?', async () => {
    setReordering(true)
    try {
      await api.admin.reorderInsuranceSteps(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
    finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<StepFormData>({ defaultValues: stepEmpty })

  const create = () => { setEditId(null); reset(stepEmpty); setOpen(true) }
  const edit = (i: InsuranceStep) => {
    setEditId(i._id)
    reset({ step: i.step, title: i.title, description: i.description, order: i.order })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(stepEmpty) }
  const onSubmit = handleSubmit(async (form) => {
    try {
      if (editId) await api.admin.updateInsuranceStep(editId, { ...form, type })
      else await api.admin.createInsuranceStep({ ...form, type })
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const remove = (id: string) => confirmDelete('Энэ бичлэгийг устгах уу?', async () => {
    try { await api.admin.deleteInsuranceStep(id); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const toggle = async (i: InsuranceStep) => {
    try { await api.admin.updateInsuranceStepStatus(i._id, !i.isActive); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>{sectionTitle} ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Алхам</Table.Th><Table.Th>Гарчиг</Table.Th><Table.Th>Тайлбар</Table.Th><Table.Th>Статус</Table.Th><Table.Th w={170}>Үйлдэл</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {items.map((i, idx) => (
              <Table.Tr key={i._id}>
                <Table.Td><Text size="sm" fw={700}>{i.step}</Text></Table.Td>
                <Table.Td><Text size="sm" fw={600}>{i.title}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{i.description}</Text></Table.Td>
                <Table.Td><Badge color={i.isActive ? 'green' : 'gray'} variant="light" size="sm">{i.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                <Table.Td><Group gap={4}>
                  <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                  <ActionIcon variant="light" size="sm" onClick={() => toggle(i)}>{i.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                  <ActionIcon variant="light" size="sm" onClick={() => edit(i)}><IconPencil size={14} /></ActionIcon>
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(i._id)}><IconTrash size={14} /></ActionIcon>
                </Group></Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </div>

      <Modal opened={open} onClose={close} title={editId ? 'Засах' : 'Шинэ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="step" control={control} rules={{ validate: (v) => v.trim() ? true : 'Алхам шаардлагатай' }} render={({ field }) => <TextInput label="Алхам * (ж: 01)" {...field} error={errors.step?.message} />} />
            <Controller name="title" control={control} rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }} render={({ field }) => <TextInput label="Гарчиг *" {...field} error={errors.title?.message} />} />
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />} />
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />
            <Group justify="flex-end">
              <Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
